/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { LocationDescriptorObject, LocationState } from "history";
import { useUrlHooks } from "hooks";
import { ExtractRouteParams, RoutePath, RoutesKeys, RoutesValues, UrlQueryParams } from "store/types";
import { JSON_to_URLEncoded } from "utils/json-to-url-encoded";
import buildNavPath from "utils/build-nav-path";

import { Routes } from "../../constants/routes";

type WithSearch = "add" | "replace";
type NoSearch = "drop" | "keep";

export type NavigationSettings<R extends RoutesKeys> = {
  route: R;
  params: ExtractRouteParams<RoutePath<R>>;
  search?: UrlQueryParams;
  redirectMethod?: "push" | "replace";
  // setQueriesMethod?: IsEmptyObject<S> extends IsEmptyObject<{}> ? NoSearch : WithSearch;
  setQueriesMethod?: NoSearch | WithSearch; // Should depend on search
  state?: LocationState;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type NavigateSettingsLimited<R extends RoutesKeys> =
  RoutePath<R> extends `${infer _}/:${infer _}`
    ? NavigationSettings<R>
    : Omit<NavigationSettings<R>, "params"> & Partial<Pick<NavigationSettings<R>, "params">>;

type Handler = <R extends RoutesKeys>(settings: NavigateSettingsLimited<R>) => void;

export function useNavigate(): Handler {
  const { push, replace } = useHistory();
  const { queryParams } = useUrlHooks();
  return useCallback(
    (settings) => {
      try {
        const { setQueriesMethod = "replace", search, state, redirectMethod = "push", route, params = {} } = settings;

        const validParams = Object.fromEntries(
          Object.entries(params).filter(([_, value]) => value !== null && value !== undefined)
        );

        const redirectPath = buildNavPath(Routes[route], validParams as ExtractRouteParams<RoutesValues>);
        if (!redirectPath) throw new Error("Can't build redirect path");

        let searchParams: UrlQueryParams = {};

        if (search) {
          if (setQueriesMethod === "replace") {
            searchParams = search;
          }

          if (setQueriesMethod === "add" || setQueriesMethod === "keep") {
            searchParams = { ...queryParams, ...search };
          }
        }

        if (!search) {
          if (setQueriesMethod === "keep") {
            searchParams = queryParams;
          }

          if (setQueriesMethod === "drop") {
            searchParams = {};
          }
        }

        const encodedSearch = JSON_to_URLEncoded(searchParams);

        const redirect: LocationDescriptorObject = {
          pathname: redirectPath,
          state
        };

        if (encodedSearch) {
          redirect.search = encodedSearch;
        }

        if (redirectMethod === "push") {
          push(redirect);
        } else {
          replace(redirect);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [queryParams, push, replace]
  );
}
